import { useMemo, useState } from 'react';
import { LuPlus } from 'react-icons/lu';
import styled from 'styled-components';

import { CommandCenterClientProfile, PersonOrgProfile, PersonOrgType } from '@ndvr/customer-api';
import { PortfolioProfile } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { OrgDrawer, OrgType } from 'modules/OrgDrawer';
import { formatCurrency, getClientName, getClientType, useRequiredParams } from 'utils';
import { useHouseholdRouteType } from 'utils/hooks/useHouseholdRoutePaths';
import { useClientProfilesForHousehold, useDeleteClient } from 'views/client/client-queries';
import { ClientDrawer } from 'views/client/ClientDrawer';
import { HouseholdHeaderActionsPortal } from 'views/household/HouseholdIndividualPage';
import { PortfolioProfileTable } from 'views/portfolio/PortfolioProfileTable';
import { HorizontalLayout, IconEdit, IconTrash, PageSpinner, Text, TextLink, VerticalLayout } from 'widgets';
import { DeleteValidationModal } from 'widgets/delete/DeleteValidationModal';
import { DropdownMenu } from 'widgets/Dropdown';

import { LinkPortfolioDrawer } from './LinkPortfolioDrawer';
import { OutstandingPortfoliosList } from './OutstandingPortfoliosList';
import { UnlinkedPortfoliosList } from './UnlinkedPortfoliosList';

interface CommandCenterClientProfileWithTotalAUM extends CommandCenterClientProfile {
    totalAUM: number | undefined;
}

export const ClientsTab = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { data: clientProfiles, isPending } = useClientProfilesForHousehold(globalAccountGUID);
    const { householdTypeLabel } = useHouseholdRouteType();

    const [showClientCreateDrawer, setShowClientCreateDrawer] = useState(false);

    const { mutateAsync: deleteClient, isPending: isDeletePending } = useDeleteClient(globalAccountGUID);
    const [clientToDelete, setClientToDelete] = useState<CommandCenterClientProfile>();

    const sortedClients = useMemo(() => {
        return clientProfiles
            ?.map((clientProfile) => ({
                ...clientProfile,
                totalAUM: calculateTotalAUM(clientProfile.portfolioProfiles),
            }))
            .sort((clientA, clientB) => {
                // sort by AUM
                if (!clientA.totalAUM) {
                    return 1;
                }
                if (!clientB.totalAUM) {
                    return -1;
                }
                return clientB.totalAUM - clientA.totalAUM;
            });
    }, [clientProfiles]);

    return (
        <VerticalLayout $gap={32}>
            <HouseholdHeaderActionsPortal>
                <div className="flex items-center gap-4">
                    <Button variant="outline2" size="sm" onClick={() => setShowClientCreateDrawer(true)}>
                        <LuPlus className="h-4 w-4" />
                        <span>Account Owner</span>
                    </Button>
                </div>
            </HouseholdHeaderActionsPortal>

            <OutstandingPortfoliosList />

            <VerticalLayout $gap={16}>
                {!isPending ? (
                    clientProfiles?.length === 0 ? (
                        <HorizontalLayout $rowCenter $center>
                            <Text largeBody>{householdTypeLabel} does not have any Account Owners</Text>
                        </HorizontalLayout>
                    ) : (
                        <VerticalLayout $gap={16}>
                            {sortedClients?.map((clientProfile, idx) => (
                                <ClientListItem
                                    key={clientProfile.client.clientGUID}
                                    clientProfile={clientProfile}
                                    hidePortfolioProfileTableHeader={idx > 0}
                                    setClientToDelete={setClientToDelete}
                                />
                            ))}
                        </VerticalLayout>
                    )
                ) : (
                    <PageSpinner />
                )}
            </VerticalLayout>

            {showClientCreateDrawer && <ClientDrawer onClose={() => setShowClientCreateDrawer(false)} />}
            {clientToDelete && (
                <DeleteValidationModal
                    recordName={`Account-owner record for ${getClientName(clientToDelete)}`}
                    disabled={isDeletePending}
                    onDelete={() => deleteClient(clientToDelete.client.clientGUID)}
                    onClose={() => setClientToDelete(undefined)}
                />
            )}

            <UnlinkedPortfoliosList />
        </VerticalLayout>
    );
};

const ClientListItem = ({
    clientProfile,
    hidePortfolioProfileTableHeader,
    setClientToDelete,
}: {
    clientProfile: CommandCenterClientProfileWithTotalAUM;
    hidePortfolioProfileTableHeader: boolean;
    setClientToDelete: (clientProfile: CommandCenterClientProfile) => void;
}) => {
    const [showClientEditDrawer, setShowClientEditDrawer] = useState(false);
    const [clientProfileToLink, setClientProfileToLink] = useState<CommandCenterClientProfile | undefined>(undefined);
    const [orgToEdit, setOrgToEdit] = useState<PersonOrgProfile | undefined>(undefined);

    return (
        <>
            <Card>
                <HouseholdInfo $rowCenter>
                    <VerticalLayout>
                        {clientProfile.clientPersonOrgProfile.personOrg.personOrgType === PersonOrgType.PERSON ? (
                            <TextLink to={`/person/${clientProfile.clientPersonOrgProfile.personOrgGUID}`}>
                                {getClientName(clientProfile)}
                            </TextLink>
                        ) : clientProfile.clientPersonOrgProfile.personOrg.personOrgType ===
                          PersonOrgType.JOINT_PERSONS ? (
                            <Text onClick={() => setShowClientEditDrawer(true)}>{getClientName(clientProfile)}</Text>
                        ) : (
                            <Text onClick={() => setOrgToEdit(clientProfile.clientPersonOrgProfile)}>
                                {getClientName(clientProfile)}
                            </Text>
                        )}
                        <Text $themeColor="subtle">
                            {getClientType(clientProfile.clientPersonOrgProfile.personOrg)}
                        </Text>
                    </VerticalLayout>
                    <HorizontalLayout $gap={clientProfile.totalAUM ? 4 : 16}>
                        <Text>{formatCurrency(clientProfile.totalAUM, 'USD')}</Text>
                        <Text>AUM</Text>
                    </HorizontalLayout>
                    <Text>{clientProfile.portfolioProfiles.length} accounts</Text>
                    <HorizontalLayout $pullRight $gap={8}>
                        <Button size="sm" variant="ghost" onClick={() => setClientProfileToLink(clientProfile)}>
                            Link Account to Owner
                        </Button>
                        <ClientOptionsMenu clientProfile={clientProfile} setClientToDelete={setClientToDelete} />
                    </HorizontalLayout>
                </HouseholdInfo>
                {clientProfile.portfolioProfiles.length > 0 && (
                    <PortfolioProfileTable
                        portfolioProfiles={clientProfile.portfolioProfiles}
                        hideHeader={hidePortfolioProfileTableHeader}
                    />
                )}
            </Card>

            {clientProfileToLink && (
                <LinkPortfolioDrawer
                    clientProfile={clientProfileToLink}
                    onClose={() => setClientProfileToLink(undefined)}
                />
            )}

            {showClientEditDrawer && (
                <ClientDrawer
                    clientGUID={clientProfile.client.clientGUID}
                    onClose={() => setShowClientEditDrawer(false)}
                />
            )}

            {orgToEdit && (
                <OrgDrawer
                    onClose={() => setOrgToEdit(undefined)}
                    orgType={orgToEdit.personOrg.personOrgType as OrgType}
                    orgGUID={orgToEdit.personOrgGUID}
                />
            )}
        </>
    );
};

const HouseholdInfo = styled(HorizontalLayout)`
    display: grid;
    grid-template-columns: 562px 170px 1fr 250px;
    padding: 16px;

    &:not(:last-child) {
        border-bottom: 1px solid ${({ theme }) => theme.border.light};
    }
`;

const calculateTotalAUM = (portfolioProfiles: PortfolioProfile[]) => {
    return portfolioProfiles.reduce<number | undefined>((res, portfolioProfile) => {
        if (!portfolioProfile.performance?.marketValue) {
            return res;
        }

        return (res || 0) + Number(portfolioProfile.performance.marketValue);
    }, undefined);
};

const ClientOptionsMenu = ({
    clientProfile,
    setClientToDelete,
}: {
    clientProfile: CommandCenterClientProfileWithTotalAUM;
    setClientToDelete: (clientProfile: CommandCenterClientProfile) => void;
}) => {
    const [showClientEditDrawer, setShowClientEditDrawer] = useState(false);

    const editButton = {
        button: (
            <HorizontalLayout $apart $rowCenter>
                <Text>Edit</Text>
                <IconEdit size="micro" />
            </HorizontalLayout>
        ),
        onClick: () => setShowClientEditDrawer(true),
    };

    const removeButton = {
        button: (
            <HorizontalLayout $apart $rowCenter>
                <Text>Delete account-owner record</Text>
                <IconTrash size="micro" />
            </HorizontalLayout>
        ),
        onClick: () => setClientToDelete(clientProfile),
    };

    return (
        <>
            <DropdownMenu placement="bottom-end" buttons={[editButton, removeButton]} />
            {showClientEditDrawer && (
                <ClientDrawer
                    clientGUID={clientProfile.client.clientGUID}
                    onClose={() => setShowClientEditDrawer(false)}
                />
            )}
        </>
    );
};
